import { BehaviorSubject } from "rxjs";
import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { CoreError, CoreErrorCause } from "../models/CoreError";
import { OnboardingState } from "../models/OnboardingState";
import { RootStateNavigator } from "./models/RootStateNavigator";
import { OnboardingFeature } from "../onboarding/onboardingFeature";
import { AnalyticsFeature } from "../analytics/analyticsFeature";
import { AnalyticsUserPropertiesFeature } from "../analytics/analyticsUserPropertiesFeature";
import { DailyCardsExperimentFeature } from "../abTesting/dailyCardsExperimentFeature";

export class AppStateFeature {
  // Singleton instance
  private static instance: AppStateFeature;

  // Internal state
  private didFinishLoadingInitialData: boolean = false;
  private isAuthenticated: boolean = false;
  private hasResolvedOnboardingState: boolean = false;
  private onboardingState: OnboardingState | null = null;
  private rootStateNavigator$: BehaviorSubject<RootStateNavigator>;
  private onboardingResolutionVersion: number = 0;
  private onboardingRetryTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly MAX_ONBOARDING_RESOLVE_RETRIES = 4;
  private static readonly ONBOARDING_RESOLVE_RETRY_DELAYS_MS = [
    250, 500, 1000, 2000,
  ];

  // Private constructor to enforce singleton usage
  private constructor() {
    // Initialize BehaviorSubject with initial computed state
    this.rootStateNavigator$ = new BehaviorSubject<RootStateNavigator>(
      this.computeRootStateNavigator(),
    );
    // Start observing auth state
    this.observeAuthStateInternal();
  }

  // Global accessor to retrieve the singleton instance
  public static getInstance(): AppStateFeature {
    if (!AppStateFeature.instance) {
      AppStateFeature.instance = new AppStateFeature();
    }
    return AppStateFeature.instance;
  }

  /**
   * Observes changes to the root state navigator.
   * The root state is determined based on:
   * - Whether initial data has finished loading
   * - Whether the user is authenticated
   *
   * Returns an unsubscribe function so callers can clean up.
   */
  public observeAppStateFeature(
    onNext: (rootState: RootStateNavigator) => void,
  ): () => void {
    // Subscribe to BehaviorSubject - this will immediately emit current value
    const subscription = this.rootStateNavigator$.subscribe({
      next: onNext,
    });

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  }

  /**
   * Sets whether initial data has finished loading.
   * This will trigger the callback to evaluate and return the new root state navigator.
   */
  public setInitialDataFinishedLoading(didFinishLoading: boolean): void {
    console.log("[AppState] Initial data finished loading:", didFinishLoading);
    this.didFinishLoadingInitialData = didFinishLoading;
    // Notify all observers with the new root state
    this.notifyObservers();
  }

  /**
   * Refreshes onboarding state after onboarding completion.
   */
  public refreshOnboardingState(): void {
    this.startOnboardingResolution();
  }

  /**
   * Computes the current root state navigator based on app state.
   * @private
   */
  private computeRootStateNavigator(): RootStateNavigator {
    // If initial data hasn't finished loading, show splash
    if (!this.didFinishLoadingInitialData) {
      return RootStateNavigator.splash;
    }

    // If user is authenticated, show partners (or onboarding if needed)
    if (this.isAuthenticated) {
      if (!this.hasResolvedOnboardingState) {
        return RootStateNavigator.splash;
      }

      if (this.onboardingState) {
        return RootStateNavigator.onboarding;
      }

      return RootStateNavigator.partners;
    }

    // Not authenticated - show auth flow
    return RootStateNavigator.auth;
  }

  /**
   * Notifies all observers with the current root state navigator.
   * @private
   */
  private notifyObservers(): void {
    const rootState = this.computeRootStateNavigator();
    this.rootStateNavigator$.next(rootState);
  }

  /**
   * Internal method to observe auth state from AuthenticationService.
   * Updates internal state and notifies observers when auth state changes.
   * @private
   */
  private observeAuthStateInternal(): void {
    const authService = AuthenticationService.getInstance();
    // This subscription lives for the entire app lifetime
    authService.observeAuthState((authUser) => {
      // Update internal authentication state
      this.isAuthenticated = !!authUser;
      AnalyticsFeature.setUserId(authUser?.id ?? null).catch(() => undefined);

      if (!authUser) {
        AnalyticsUserPropertiesFeature.stopForAuthenticatedUser();
        DailyCardsExperimentFeature.stopForAuthenticatedUser();
        this.cancelPendingOnboardingRetry();
        this.onboardingResolutionVersion += 1;
        this.onboardingState = null;
        this.hasResolvedOnboardingState = false;
        this.notifyObservers();
        return;
      }

      AnalyticsUserPropertiesFeature.startForAuthenticatedUser(authUser.id);
      DailyCardsExperimentFeature.startForAuthenticatedUser(authUser.id);

      this.startOnboardingResolution();
    });
  }

  private startOnboardingResolution(): void {
    this.cancelPendingOnboardingRetry();
    this.onboardingResolutionVersion += 1;
    this.hasResolvedOnboardingState = false;
    this.notifyObservers();
    void this.resolveOnboardingState(this.onboardingResolutionVersion, 0);
  }

  private async resolveOnboardingState(
    resolutionVersion: number,
    attempt: number,
  ): Promise<void> {
    if (
      !this.isAuthenticated ||
      resolutionVersion !== this.onboardingResolutionVersion
    ) {
      return;
    }

    try {
      this.onboardingState = await OnboardingFeature.getRequiredOnboardingState(
        {
          suppressMissingProfileReporting: true,
        },
      );
      if (
        resolutionVersion !== this.onboardingResolutionVersion ||
        !this.isAuthenticated
      ) {
        return;
      }

      this.hasResolvedOnboardingState = true;
      this.notifyObservers();
    } catch (error) {
      if (
        resolutionVersion !== this.onboardingResolutionVersion ||
        !this.isAuthenticated
      ) {
        return;
      }

      if (this.shouldRetryOnboardingResolution(error, attempt)) {
        this.scheduleOnboardingRetry(resolutionVersion, attempt + 1);
        return;
      }

      console.error(
        "[AppState] Failed to resolve onboarding state, routing to onboarding:",
        error,
      );
      this.onboardingState = OnboardingState.Name;
      this.hasResolvedOnboardingState = true;
      this.notifyObservers();
    }
  }

  private shouldRetryOnboardingResolution(
    error: unknown,
    attempt: number,
  ): boolean {
    if (attempt >= AppStateFeature.MAX_ONBOARDING_RESOLVE_RETRIES) {
      return false;
    }

    if (!(error instanceof CoreError)) {
      return false;
    }

    if (
      error.cause === CoreErrorCause.DataNotFound ||
      error.cause === CoreErrorCause.NetworkConnection ||
      error.cause === CoreErrorCause.NetworkError
    ) {
      return true;
    }

    return (
      error.cause === CoreErrorCause.Unknown &&
      (error.context?.includes("User with identifier") ||
        error.context?.includes("does not exist") ||
        false)
    );
  }

  private scheduleOnboardingRetry(
    resolutionVersion: number,
    nextAttempt: number,
  ): void {
    const retryDelayMs =
      AppStateFeature.ONBOARDING_RESOLVE_RETRY_DELAYS_MS[nextAttempt - 1] ??
      2000;

    this.cancelPendingOnboardingRetry();
    this.onboardingRetryTimer = setTimeout(() => {
      this.onboardingRetryTimer = null;
      void this.resolveOnboardingState(resolutionVersion, nextAttempt);
    }, retryDelayMs);
  }

  private cancelPendingOnboardingRetry(): void {
    if (!this.onboardingRetryTimer) {
      return;
    }

    clearTimeout(this.onboardingRetryTimer);
    this.onboardingRetryTimer = null;
  }
}
